import { css } from '@emotion/react';
import { ReactElement, RefObject, useEffect, useState } from 'react';

import { IS_MAC } from '../../../../app/App';
import { styledWithoutTheme } from '../../../../styled';
import type { Theme } from '../../../../theme/interface/detailed';
import { isMobile } from '../../../../utils/isMobile';
import { useSizes } from '../messageField/suggestions/hooks/useSizes';
import { GluedTooltip } from './gluedTooltop';
import { StickyTooltip } from './stickyTooltip';

export const TOOLTIP_HALF_RHOMBUS = 8;

const defaultStyles = css`
	background: #464c59;
	border-radius: 4px;
	color: #fff;
	font-size: 12px;
	left: 50%;
	padding: 5px;
	position: absolute;
	text-align: center;
	transform: translateX(-50%);
	width: min(150px, 50%);
	z-index: 10;
	&::after {
		background: #464c59;
		content: ' ';
		height: ${TOOLTIP_HALF_RHOMBUS * 2}px;
		width: ${TOOLTIP_HALF_RHOMBUS * 2}px;
		left: calc(50% - 8px);
		position: absolute;
		transform: rotate(45deg);
		z-index: -1;
	}
`;

type Interface = {
	children: string;
	className?: string;
	fieldRef?: RefObject<HTMLDivElement>;
	setShowTooltip?: React.Dispatch<React.SetStateAction<boolean>>;
};

const Component = ({ className, children, fieldRef, setShowTooltip }: Interface): ReactElement => {
	const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>('top');
	const { tooltipRef, styles } = useSizes(children.length, tooltipPosition);

	useEffect(() => {
		if (setShowTooltip) {
			const fn = (): void => setShowTooltip(false);
			if (fieldRef?.current) {
				document.addEventListener('wheel', fn);
				document.addEventListener('touchstart', fn);
				return (): void => {
					document.removeEventListener('wheel', fn);
					document.removeEventListener('touchstart', fn);
				};
			}
		}
	}, [fieldRef, setShowTooltip]);

	return !IS_MAC && !isMobile ? (
		<StickyTooltip fieldRef={fieldRef} tooltipRef={tooltipRef} styles={styles} className={className}>
			{children}
		</StickyTooltip>
	) : (
		<GluedTooltip
			fieldRef={fieldRef}
			tooltipRef={tooltipRef}
			styles={styles}
			className={className}
			tooltipPosition={tooltipPosition}
			setTooltipPosition={setTooltipPosition}
		>
			{children}
		</GluedTooltip>
	);
};

export const Tooltip = styledWithoutTheme(Component)(
	({ theme }: { theme: Theme }) => ({
		color: theme.allSettings.fields.common.errorColor,
		fontFamily: theme.allSettings.containers.mainFonts.fontFamily,
	}),
	defaultStyles
);
